import { viewer as view } from '/@/hooks/Cesium/use-cesium-map'
import * as Cesium from 'cesium'

// 单个掩膜实例的存储结构
interface MaskInstance {
  id: string
  primitives: Cesium.Primitive[]
  entities: Cesium.Entity[]
  dataSource?: Cesium.GeoJsonDataSource
  originalGlobeColor?: Cesium.Color
  // 可扩展其他属性
}

const BorderMaskManager = {
  // 存储所有掩膜实例（用 id 索引）
  masks: new Map<string, MaskInstance>(),

  // 全局备份（如果需要保留之前的数组兼容性，可以继续保留，但建议用 masks）
  // maskPrimitives: [],
  // maskEntities: [],
  // maskPolygons: [],
  // currentDataSource: null,
  // originalGlobeColor: null,

  /**
   * 创建带唯一标识的掩膜
   * @param geoJsonUrl GeoJSON路径
   * @param options 配置选项（可包含 id）
   * @returns Promise<{ id: string, dataSource, primitives, entities }>
   */
  createBorderMask: async function (geoJsonUrl: string, options: any = {}) {
    const defaultOptions = {
      id: Date.now().toString() + '_' + Math.random(), // 默认唯一ID
      borderColor: Cesium.Color.YELLOW,
      borderWidth: 5,
      borderOpacity: 0.8,
      maskShow: false,
      maskOpacity: 0.7,
      maskColor: Cesium.Color.BLACK,
      enableGlow: true,
      glowPower: 0.1
    }
    const config = { ...defaultOptions, ...options }
    const viewer = view || this._getViewer()

    // 如果已经存在同 id 的掩膜，先移除
    if (this.masks.has(config.id)) {
      console.warn(`Mask with id ${config.id} already exists, removing old one.`)
      await this.removeMask(config.id)
    }

    // 保存原始地球颜色（如果是第一次创建全局掩膜？注意：每个掩膜独立处理可能冲突，建议在 remove 时恢复）
    // 这里简化：每个掩膜不修改地球 baseColor，避免互相干扰。如需全局变暗，使用矩形覆盖层即可。
    // 原代码的 global mask 使用了 rectangle，不需要改 baseColor。

    let fill = config.maskColor.withAlpha(config.maskOpacity)
    if (!config.maskShow) {
      fill = Cesium.Color.TRANSPARENT
    }

    // 1. 加载 GeoJSON
    const dataSource = await Cesium.GeoJsonDataSource.load(geoJsonUrl, {
      stroke: config.borderColor,
      strokeWidth: config.borderWidth,
      fill: fill,
      strokeOpacity: config.borderOpacity
    })
    viewer.dataSources.add(dataSource)

    // 存储当前掩膜的相关对象
    const maskInstance: MaskInstance = {
      id: config.id,
      primitives: [],
      entities: [],
      dataSource: dataSource
    }

    // 2. 处理区域：创建全局暗化层 + 高亮边框
    this._processRegionsForInstance(dataSource, config, fill, maskInstance)

    // 3. 可选：调整视角（注释掉）
    // this._zoomToRegion(dataSource)

    // 存储到 Map
    this.masks.set(config.id, maskInstance)

    return {
      id: config.id,
      dataSource: dataSource,
      primitives: maskInstance.primitives,
      entities: maskInstance.entities
    }
  },

  /**
   * 处理某个数据源，并将创建的图元存入指定实例
   */
  _processRegionsForInstance: function (dataSource: Cesium.GeoJsonDataSource, config: any, fill: Cesium.Color, instance: MaskInstance) {
    const viewer = view || this._getViewer()
    const entities = dataSource.entities.values

    // 设置多边形填充透明（如果需要）
    entities.forEach((entity) => {
      if (entity.polygon) {
        entity.polygon.material = fill
        entity.polygon.outline = false
        entity.polygon.show = true
      }
    })

    // 为每个多边形创建对应的洞和边框
    entities.forEach((entity) => {
      if (entity.polygon) {
        const hierarchy = entity.polygon.hierarchy.getValue(Cesium.JulianDate.now())
        if (hierarchy && hierarchy.positions) {
          // 创建区域洞（在全局暗化层上挖洞）
          const regionPrimitive = this._createRegionHolePrimitive(hierarchy.positions, config)
          if (regionPrimitive) {
            viewer.scene.primitives.add(regionPrimitive)
            instance.primitives.push(regionPrimitive)
          }
          // 创建高亮边框
          const borderEntity = this._createBorderEntity(hierarchy.positions, config)
          viewer.entities.add(borderEntity)
          instance.entities.push(borderEntity)
          // 发光效果
          if (config.enableGlow) {
            const glowEntity = this._createGlowEntity(hierarchy.positions, config)
            viewer.entities.add(glowEntity)
            instance.entities.push(glowEntity)
          }
        }
      }
    })
  },

  /**
   * 创建单个区域的洞图元（返回 Primitive 对象）
   */
  _createRegionHolePrimitive: function (positions: Cesium.Cartesian3[], config: any): Cesium.Primitive | null {
    // 复用原 _createRegionHole 的逻辑，但返回 primitive 以便管理
    const polygonGeometry = new Cesium.PolygonGeometry({
      polygonHierarchy: new Cesium.PolygonHierarchy(positions),
      perPositionHeight: true,
      vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT
    })
    const geometry = Cesium.PolygonGeometry.createGeometry(polygonGeometry)
    if (!geometry) return null
    const instance = new Cesium.GeometryInstance({
      geometry: geometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(Cesium.Color.TRANSPARENT)
      }
    })
    const primitive = new Cesium.Primitive({
      geometryInstances: instance,
      appearance: new Cesium.PerInstanceColorAppearance({
        translucent: true,
        flat: true,
        renderState: {
          depthTest: { enabled: true },
          stencilTest: {
            enabled: true,
            frontFunction: Cesium.StencilFunction.NOT_EQUAL,
            frontOperation: { fail: Cesium.StencilOperation.KEEP, zFail: Cesium.StencilOperation.KEEP, zPass: Cesium.StencilOperation.REPLACE },
            backFunction: Cesium.StencilFunction.NOT_EQUAL,
            backOperation: { fail: Cesium.StencilOperation.KEEP, zFail: Cesium.StencilOperation.KEEP, zPass: Cesium.StencilOperation.REPLACE },
            reference: 1,
            mask: 0xFF
          },
          colorMask: { red: false, green: false, blue: false, alpha: false }
        }
      }),
      asynchronous: false
    })
    return primitive
  },

  /**
   * 创建边框实体
   */
  _createBorderEntity: function (positions: Cesium.Cartesian3[], config: any): Cesium.Entity {
    return new Cesium.Entity({
      polyline: {
        positions: positions,
        width: config.borderWidth,
        material: config.borderColor.withAlpha(config.borderOpacity),
        clampToGround: true
      }
    })
  },

  /**
   * 创建发光实体
   */
  _createGlowEntity: function (positions: Cesium.Cartesian3[], config:any): Cesium.Entity {
    return new Cesium.Entity({
      polyline: {
        positions: positions,
        width: 20,
        material: new Cesium.PolylineGlowMaterialProperty({
          glowPower: config.glowPower,
          color: config.borderColor.withAlpha(config.borderOpacity)
        }),
        clampToGround: true
      }
    })
  },

  /**
   * 根据 id 删除掩膜
   */
  removeMask: function (id: string) {
    const instance = this.masks.get(id)
    if (!instance) {
      console.warn(`Mask with id ${id} not found.`)
      return false
    }
    const viewer = view || this._getViewer()
    // 移除所有图元
    instance.primitives.forEach(primitive => {
      viewer.scene.primitives.remove(primitive)
    })
    // 移除所有实体
    instance.entities.forEach(entity => {
      viewer.entities.remove(entity)
    })
    // 移除数据源
    if (instance.dataSource) {
      viewer.dataSources.remove(instance.dataSource)
    }
    // 可选：恢复地球颜色（如果修改过，但这里每个掩膜未修改全局颜色，故不需要）
    this.masks.delete(id)
    console.log(`Mask ${id} removed.`)
    return true
  },

  /**
   * 隐藏掩膜（设置图元/实体可见性为 false）
   */
  hideMask: function (id: string, hide: boolean = true) {
    const instance = this.masks.get(id)
    if (!instance) return false
    instance.primitives.forEach(primitive => {
      primitive.show = !hide
    })
    instance.entities.forEach(entity => {
      entity.show = !hide
    })
    if (instance.dataSource) {
      instance.dataSource.show = !hide
    }
    return true
  },

  /**
   * 更新某个掩膜的边框颜色
   */
  updateBorderColor: function (id: string, color: Cesium.Color) {
    const instance = this.masks.get(id)
    if (!instance) return false
    instance.entities.forEach(entity => {
      if (entity.polyline) {
        entity.polyline.material = color
      }
    })
    return true
  },

  /**
   * 更新边框宽度
   */
  updateBorderWidth: function (id: string, width: number) {
    const instance = this.masks.get(id)
    if (!instance) return false
    instance.entities.forEach(entity => {
      if (entity.polyline) {
        entity.polyline.width = width
      }
    })
    return true
  },

  /**
   * 移除所有掩膜（兼容原接口）
   */
  removeBorderMask: function () {
    const ids = Array.from(this.masks.keys())
    ids.forEach(id => this.removeMask(id))
  },

  /**
   * 获取 viewer 实例
   */
  _getViewer: function () {
    if (!view) throw new Error('请先初始化Cesium viewer')
    return view
  }
}

export default BorderMaskManager
